import React, { Suspense, lazy } from "react";
import { Routes as RouterRoutes, Route, Navigate, useLocation } from "react-router-dom";
import HomeHero from "../../components/HomeHero/HomeHero";
import NewPatientPaperworkBanner from "../../components/NewPatientPaperworkBanner/NewPatientPaperworkBanner";

/**
 * Route-level code splitting (perf): every page container is lazy-loaded so the
 * initial bundle ships only the shell + the route actually requested, instead of
 * one ~1.4MB monolith. Header/Footer stay eager (in App.js) since they render on
 * every route. Each container below becomes its own webpack chunk.
 */
const Home = lazy(() => import("../../containers/Home/Home"));
const ServicePage = lazy(() => import("../../containers/ServicePage/ServicePage"));
const LocationPage = lazy(() => import("../../containers/LocationPage/LocationPage"));
const Team = lazy(() => import("../../containers/Team/Team"));
const Injuries = lazy(() => import("../../containers/Injuries/Injuries"));
const Contact = lazy(() => import("../../containers/Contact/Contact"));
const RequestAppointment = lazy(() => import("../../containers/RequestAppointment/RequestAppointment"));
const NotFound = lazy(() => import("../../containers/NotFound/NotFound"));
const BlogIndex = lazy(() => import("../../containers/Blog/BlogIndex"));
const BlogPost = lazy(() => import("../../containers/Blog/BlogPost"));
const BlogCategory = lazy(() => import("../../containers/Blog/BlogCategory"));
const Resources = lazy(() => import("../../containers/Resources/Resources"));
const FAQ = lazy(() => import("../../containers/FAQ/FAQ"));
const CoreExercises = lazy(() => import("../../containers/CoreExercises/CoreExercises"));
const MediaCenter = lazy(() => import("../../containers/MediaCenter/MediaCenter"));
const Videos = lazy(() => import("../../containers/Videos/Videos"));
const PatientReferral = lazy(() => import("../../containers/PatientReferral/PatientReferral"));
const NewPatientForms = lazy(() => import("../../containers/NewPatientForms/NewPatientForms"));
const NewPatientPaperwork = lazy(() => import("../../containers/NewPatientPaperwork/NewPatientPaperwork"));
const InjuryHub = lazy(() => import("../../containers/InjuryHub/InjuryHub"));
const SpecialtyPage = lazy(() => import("../../containers/SpecialtyPage/SpecialtyPage"));
const AboutUs = lazy(() => import("../../containers/AboutUs/AboutUs"));
const Services = lazy(() => import("../../containers/Services/Services"));
const Locations = lazy(() => import("../../containers/Locations/Locations"));
const LegalPage = lazy(() => import("../../containers/LegalPage/LegalPage"));
const ConditionPage = lazy(() => import("../../containers/ConditionPage/ConditionPage"));
const LegalIntent = lazy(() => import("../../containers/LegalIntent/LegalIntent"));
const BodyPartPage = lazy(() => import("../../containers/BodyPartPage/BodyPartPage"));
const KneePainTreatment = lazy(() => import("../../containers/KneePainTreatment/KneePainTreatment"));

/**
 * Language-prefixed route wrapper.
 * Generates routes for all 6 locales: /, /es, /pt, /ko, /ja, /fr
 */
function LangRoutes({ path, element }) {
  const prefixes = ['', '/es', '/pt', '/ko', '/ja', '/fr'];
  return prefixes.map(prefix => (
    <Route key={prefix + path} path={prefix + path} element={element} />
  ));
}

/**
 * Known locale codes for explicit locale-in-URL pSEO routes.
 * These routes use /:locale/ as the first segment (e.g. /en/, /es/, etc.)
 * React Router ranks static segments higher than dynamic ones, so
 * /en/:slug beats /:locationSlug/:serviceSlug when first segment === 'en'.
 */
const LOCALE_CODES = ['en', 'es', 'pt', 'ko', 'ja', 'fr'];

/**
 * Body part slugs that should resolve to BodyPartPage
 * when accessed via /:locale/:bodyPartSlug
 */
const BODY_PART_SLUGS = [
  'back-injury', 'knee-injury', 'neck-injury', 'shoulder-injury',
  'hip-injury', 'wrist-injury', 'ankle-injury', 'head-injury',
  'elbow-injury', 'foot-injury', 'hand-injury', 'spine-injury',
];

/**
 * Suspense fallback for the lazy route chunks (first-load render-jump fix,
 * client report 2026-07-10).
 *
 * The old fallback was an empty 100vh div: on a cold homepage load the static
 * hero shell (shipped in index.html) painted, React mounted and clobbered it,
 * and the viewport went BLANK until the Home chunk arrived — a visible
 * hero -> blank -> hero "jump". On home routes we now render the REAL hero
 * (plus the paperwork banner, which Home also renders above it) so the
 * fallback and the mounted Home paint identical above-the-fold pixels.
 */
function SuspenseFallback() {
  const { pathname } = useLocation();
  const p = pathname.replace(/\/+$/, '') || '/';
  const isHome = p === '/' || ['/es', '/pt', '/ko', '/ja', '/fr'].includes(p);
  if (isHome) {
    return (
      <>
        <NewPatientPaperworkBanner />
        <HomeHero />
      </>
    );
  }
  // Content pages: re-paint the prerendered static body (captured in
  // src/index.js before React clobbered #root) so first load shows
  // content → content instead of content → blank → content (2026-07-17).
  const staticHtml = typeof window !== 'undefined' && window.__PRERENDER_BODY_HTML__;
  if (staticHtml) {
    return (
      <div
        aria-hidden="true"
        style={{ minHeight: '100vh' }}
        dangerouslySetInnerHTML={{ __html: staticHtml }}
      />
    );
  }
  return <div style={{ minHeight: '100vh' }} aria-hidden="true" />;
}

/**
 * Mounts only when Suspense resolves (i.e. the first route chunk has actually
 * rendered). Clearing the capture here scopes the static-body fallback to the
 * INITIAL page load — later in-app navigations must never flash the landing
 * page's prerendered content (react-router v6 keeps the old UI visible via
 * startTransition during those anyway).
 */
function ClearPrerenderCapture() {
  React.useEffect(() => {
    if (typeof window !== 'undefined') window.__PRERENDER_BODY_HTML__ = null;
  }, []);
  return null;
}

export default function Routes() {
  return (
    <Suspense fallback={<SuspenseFallback />}>
    <ClearPrerenderCapture />
    <RouterRoutes>
      {/* ─── Home ─── */}
      {LangRoutes({ path: '/', element: <Home /> })}

      {/* ─── Global / Static pages ─── */}
      {LangRoutes({ path: '/about-us', element: <AboutUs /> })}
      {LangRoutes({ path: '/services', element: <Services /> })}
      {LangRoutes({ path: '/locations', element: <Locations /> })}
      {LangRoutes({ path: '/meet-our-team', element: <Team /> })}
      {LangRoutes({ path: '/meet-our-team/:doctorSlug', element: <Team /> })}
      {LangRoutes({ path: '/injuries', element: <Injuries /> })}
      {LangRoutes({ path: '/contact', element: <Contact /> })}
      {LangRoutes({ path: '/request-appt', element: <RequestAppointment /> })}
      {/* Legacy redirect: old URLs may be indexed under /request-appointment */}
      {LangRoutes({ path: '/request-appointment', element: <Navigate to="/request-appt/" replace /> })}
      {LangRoutes({ path: '/privacy-policy', element: <Navigate to="/privacy/" replace /> })}
      {LangRoutes({ path: '/privacy', element: <LegalPage slug="privacy" /> })}
      {LangRoutes({ path: '/terms', element: <LegalPage slug="terms" /> })}
      {LangRoutes({ path: '/sms-messaging-terms-conditions', element: <Navigate to="/terms/" replace /> })}
      {LangRoutes({ path: '/ada', element: <LegalPage slug="ada" /> })}
      {LangRoutes({ path: '/accessibility', element: <Navigate to="/ada/" replace /> })}
      {LangRoutes({ path: '/sitemap', element: <LegalPage slug="sitemap" /> })}

      {/* ─── Resource pages ─── */}
      {LangRoutes({ path: '/resources', element: <Resources /> })}
      {LangRoutes({ path: '/blog', element: <BlogIndex /> })}
      {LangRoutes({ path: '/blog/category/:categorySlug', element: <BlogCategory /> })}
      {LangRoutes({ path: '/blog/:postSlug', element: <BlogPost /> })}
      {/* /faqs/ is the SINGLE canonical FAQ URL (GSC duplicate-cluster fix
          2026-07-18): the legacy FAQ aliases used to render the SAME FAQ
          content at 5 URLs x 6 locales, and Google chose the legacy
          /frequently-asked-questions/ as canonical OVER the sitemap URL.
          Amplify serves real 301s for these; the Navigates below only cover
          client-side navigation to stale in-app links. */}
      {LangRoutes({ path: '/frequently-asked-questions', element: <Navigate to="/faqs/" replace /> })}
      {LangRoutes({ path: '/faqs', element: <FAQ /> })}
      {LangRoutes({ path: '/faqs-injured-in-a-car-accident', element: <Navigate to="/faqs/" replace /> })}
      {LangRoutes({ path: '/workplace-injury-questions', element: <Navigate to="/faqs/" replace /> })}
      {LangRoutes({ path: '/sports-injury-faq', element: <Navigate to="/faqs/" replace /> })}
      {LangRoutes({ path: '/core-exercises', element: <CoreExercises /> })}
      {/* /media-center/ is the single canonical media URL; /media was a duplicate. */}
      {LangRoutes({ path: '/media', element: <Navigate to="/media-center/" replace /> })}
      {LangRoutes({ path: '/media-center', element: <MediaCenter /> })}
      {LangRoutes({ path: '/videos', element: <Videos /> })}
      {LangRoutes({ path: '/resources/patient-referral', element: <PatientReferral /> })}
      {LangRoutes({ path: '/new-patient-forms', element: <NewPatientForms /> })}
      {LangRoutes({ path: '/new-patient-paperwork', element: <NewPatientPaperwork /> })}
      {LangRoutes({ path: '/new-patient-paperwork/:state', element: <NewPatientPaperwork /> })}

      {/* ─── Injury hub pages (existing /injuries/:slug pattern) ─── */}
      {LangRoutes({ path: '/injuries/:injurySlug', element: <InjuryHub /> })}

      {/* ─── Specialty pages ─── */}
      {LangRoutes({ path: '/specialty/:specialtySlug', element: <SpecialtyPage /> })}

      {/* ─── Condition pages (existing /conditions/:slug) ─── */}
      {LangRoutes({ path: '/conditions/:conditionSlug', element: <ConditionPage /> })}

      {/* ─── Legal intent pages (existing /legal/:slug) ─── */}
      {LangRoutes({ path: '/legal/:intentSlug', element: <LegalIntent /> })}


      {/* ═══════════════════════════════════════════════════════════
          pSEO MATRIX ROUTES — Explicit locale prefix in URL
          Pattern: /:locale/:city/:service (540+420+360 = 1,320 pages)
          e.g. /en/atlanta/car-accident-doctor
          These use /:locationSlug/:serviceSlug param names so
          ServicePage.js can reuse its existing param destructuring.
          NOTE: 3-segment routes are inherently more specific than
          2-segment ones and are matched first by React Router v6.
      ═══════════════════════════════════════════════════════════ */}

      {/* pSEO: locale + city + service (3-segment) */}
      {LOCALE_CODES.map((locale) => (
        <Route
          key={`pseo-service-${locale}`}
          path={`/${locale}/:locationSlug/:serviceSlug`}
          element={<ServicePage />}
        />
      ))}

      {/* pSEO: locale + conditions + condition slug */}
      {LOCALE_CODES.map((locale) => (
        <Route
          key={`pseo-condition-${locale}`}
          path={`/${locale}/conditions/:conditionSlug`}
          element={<ConditionPage />}
        />
      ))}

      {/* pSEO: locale + legal + intent slug */}
      {LOCALE_CODES.map((locale) => (
        <Route
          key={`pseo-legal-${locale}`}
          path={`/${locale}/legal/:intentSlug`}
          element={<LegalIntent />}
        />
      ))}

      {/* pSEO: locale + body part injury slug (2-segment, static locale wins over dynamic /:locationSlug) */}
      {/* NOTE: these are STATIC paths (no :param), so the slug MUST be passed as a
          prop — useParams() is empty here. Before this fix BodyPartPage always
          rendered "Page Not Found" (soft-404) on every body-part URL. */}
      {LOCALE_CODES.flatMap((locale) =>
        BODY_PART_SLUGS.map((slug) => (
          <Route
            key={`pseo-bodypart-${locale}-${slug}`}
            path={`/${locale}/${slug}`}
            element={<BodyPartPage slug={slug} />}
          />
        ))
      )}

      {/* Canonical body-part URLs — the SITEMAP form: /<part>-injury-treatment/
          (locale-prefixed, '' = EN). These previously fell through to
          /:locationSlug -> LocationPage -> "Location Not Found" (soft-404 on 72
          sitemap URLs). Static segments outrank dynamic ones in RRv6. */}
      {['', '/es', '/pt', '/ko', '/ja', '/fr'].flatMap((prefix) =>
        BODY_PART_SLUGS.map((slug) => (
          <Route
            key={`pseo-bodypart-treatment-${prefix}-${slug}`}
            path={`${prefix}/${slug}-treatment`}
            element={<BodyPartPage slug={slug} />}
          />
        ))
      )}

      {/* ─── gso-only pillar: /knee-pain-treatment/ (B3 2026-10-05) ───
          Target: head term `knee pain treatment` (165K/mo per Google Keyword
          Planner). Hub for the gso knee geo-fan-out
          (/<city>/knee-pain-treatment/, /<city>/knee-surgery/,
          /<city>/knee-replacement-surgery/). Static path MUST precede the
          /:locationSlug catchall below so this URL does not resolve as a
          city page. Container renders NotFound on non-gso brands.
          EN-only for v1 (prerender-schema.js emits this route for gso+en
          only), so register a single plain Route rather than LangRoutes —
          the LangRoutes variant advertises /es|pt|ko|ja|fr/knee-pain-treatment/
          in the SPA route table with no prerendered file, tripping the
          verify-prerender route-table gate. */}
      <Route path="/knee-pain-treatment" element={<KneePainTreatment />} />

      {/* ─── City landing page (2-segment, no locale prefix) ─── */}
      {LangRoutes({ path: '/:locationSlug', element: <LocationPage /> })}

      {/* ─── City + Service page (2-segment, no locale prefix) ─── */}
      {LangRoutes({ path: '/:locationSlug/:serviceSlug', element: <ServicePage /> })}

      {/* ─── 404 ─── */}
      <Route path="*" element={<NotFound />} />
    </RouterRoutes>
    </Suspense>
  );
}
